<template>
  <div
    :class="{
      'bl-target-progress-container-empty': !entries || !entries.length
    }"
    class="bl-target-progress-container"
  >
    <div v-if="readonly" class="relative w-full">
      <div class="flex-shrink-0 w-full h-14" />
      <div class="absolute w-full -top-6">
        <div class="flex items-center gap-2 px-4 py-2 bg-info-100">
          <div
            class="grid flex-shrink-0 w-10 h-10 place-items-center"
            :style="{ background: '#E4F1FA', borderRadius: '0.25rem' }"
          >
            <BlIcon icon="toast-info" class="w-6 h-6" />
          </div>
          <BlTextBody1
            class="text-info"
            text="All data displayed is mock information, used for post-session visual representation."
          />
        </div>
      </div>
    </div>

    <span
      v-if="(!entries || !entries.length) && !readonly"
      class="px-6 bl-target-progress-message"
    >
      <BlTextBody2
        text="Don't see any records yet? No problem! They'll start appearing as soon as you"
      />
      <div
        class="cursor-pointer "
        @click="$router.push({ path: `/clients/${$route.params.id}/session` })"
      >
        <BlTextSubtitle3
          class="bl-target-progress-message-highlight"
          text="start sessions."
        />
      </div>
    </span>

    <div v-if="entries.length" class="px-6 bl-target-progress-graph">
      <div v-if="chartLoading" class="absolute z-20 w-full h-full">
        <div class="absolute w-full h-full opacity-75 bg-pure-white"></div>
        <div class="absolute z-20 grid w-full h-full place-items-center">
          <div class="blum-loader"></div>
        </div>
      </div>

      <div class="bl-target-progress-graph-header">
        <div class="bl-target-progress-graph-title-container">
          <BlTextSubtitle1
            class="bl-target-progress-graph-title"
            text="Progress"
          />
          <BlBubbleCount
            :count="entries.filter(i => !i.type.includes('PhaseLine')).length"
          />
        </div>
        <div
          id="chart-legends"
          class="relative flex items-center gap-4 bl-graph-legend-container"
        >
          <div class="flex items-center gap-2 ml-6">
            <BlTextBody2 text="Show" />
            <BlInputButton
              label="Grid"
              type="outlined"
              dense
              :class="chartGridShow ? 'border-light-purple' : ''"
              @clicked="chartGridShow = !chartGridShow"
            />
          </div>
        </div>
      </div>

      <div
        v-if="target.type.includes('Sbt') && chartLegendShow"
        id="task-selection-legends"
      >
        <div
          class="flex items-center flex-shrink-0 w-full gap-3 px-5 py-2 overflow-x-auto scrollbar-lg"
        >
          <div
            class="w-30 py-2.5 px-3 flex flex-col items-center justify-center gap-1 flex-shrink-0 text-center border rounded cursor-pointer"
            :class="[
              isTaskShowAll ? 'border-light-purple-300' : 'border-grey-200'
            ]"
            @click="onToggleTaskShow('allTasks', 'task')"
          >
            <BlIcon icon="prompt-line" class="flex-shrink-0 h-4" />
            <BlTextBody2 text="All Tasks" class="capitalize-first" />
          </div>
          <div
            class="w-30 py-2.5 px-3 flex flex-col items-center justify-center gap-1 overflow-hidden flex-shrink-0 text-center border rounded cursor-pointer"
            :class="[
              isPbShowAll ? 'border-light-purple-300' : 'border-grey-200'
            ]"
            @click="onToggleTaskShow('allProblemBehaviors', 'pb')"
          >
            <BlIcon icon="prompt-line" class="flex-shrink-0 h-4" />
            <BlTextBody2
              text="All Problem Behaviors"
              class="truncate capitalize-first"
              :style="{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '70px'
              }"
              title="All Problem Behaviors"
            />
          </div>
          <div
            v-for="task in defaultTasks"
            :key="`task-${task.id}`"
            class="w-30 py-2.5 px-3 flex flex-col items-center justify-center gap-1 flex-shrink-0 text-center border rounded cursor-pointer"
            :class="[
              task.is_show ? 'border-light-purple-300' : 'border-grey-200'
            ]"
            :title="task.code"
            @click="onToggleTaskShow(task, 'task')"
          >
            <div class="h-4">
              <div
                class="flex-shrink-0 w-12"
                :style="{ backgroundColor: task.color, height: '2px' }"
              />
            </div>
            <BlTextBody2
              :text="task.code"
              class="w-24 truncate capitalize-first"
            />
          </div>
          <div
            v-for="pb in defaultProblemBehaviors"
            :key="`problem-behavior-${pb.id}`"
            class="w-30 py-2.5 px-3 flex flex-col items-center justify-center gap-1 flex-shrink-0 text-center border rounded cursor-pointer"
            :class="[
              pb.is_show ? 'border-light-purple-300' : 'border-grey-200'
            ]"
            :title="pb.code"
            @click="onToggleTaskShow(pb, 'pb')"
          >
            <div class="h-4">
              <div
                class="flex-shrink-0 w-5"
                :style="{
                  backgroundColor: pb.color,
                  height: '14px',
                  borderRadius: '2px'
                }"
              />
            </div>
            <BlTextBody2
              :text="pb.code"
              class="w-24 truncate capitalize-first"
            />
          </div>
        </div>
      </div>

      <ProgressGraph
        :key="`${target.id}-${chartKey}-scrollable`"
        :graphData="chartDatas"
        :targetType="target.type"
        :chartGridShow="chartGridShow"
        :present="dataFormat"
      />
    </div>

    <div v-if="entries && entries.length" class="bl-target-progress-entries">
      <div class="flex items-center justify-between px-6 py-5">
        <div class="flex items-center gap-4">
          <BlTextSubtitle1 text="Session records" />
          <div
            id="toggle-score-precentage"
            class="p-0.5 flex justify-between border border-grey-200 rounded bg-slate-400 text-sm font-bold font-logo"
          >
            <div
              v-for="opt in dataFormatOptions"
              :key="opt.value"
              class="flex items-center justify-center h-8 rounded cursor-pointer w-30 font-body"
              :class="
                dataFormat === opt.value ? 'bg-pure-white' : 'bg-slate-400'
              "
              :title="opt.title"
              @click="dataFormat = opt.value"
            >
              <BlTextSubtitle3
                :text="opt.title"
                class="font-bold text-slate-700"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="min-w-full pb-40 overflow-x-auto scrollbar-lg">
        <div
          class="px-6 border-b border-slate-400"
          :style="{
            width:
              dataFormat === 'distribution' && defaultTasks
                ? defaultTasks.length < 8
                  ? '100%'
                  : defaultTasks.length >= 9 && defaultTasks.length < 10
                  ? 'calc(100vw - 27rem)'
                  : defaultTasks.length >= 10 && defaultTasks.length < 11
                  ? 'calc(100vw - 20rem)'
                  : 'calc(100vw - 2rem)'
                : ''
          }"
        >
          <!-- <div style="position: absolute; left: 0; bottom: 0; width: 100%; border-bottom: 1px solid #e5e7eb"></div> -->
          <div class="flex items-center flex-grow">
            <div
              id="date-header"
              class="flex items-center flex-shrink-0 w-32 h-12 px-2 border-b border-slate-400 bg-pure-white"
            >
              <BlTextBody1 text="Date" />
            </div>
            <div id="prompts-header" class="flex items-center flex-shrink-0">
              <div
                v-if="dataFormat === 'cumulative'"
                v-for="pb in defaultProblemBehaviors"
                :key="pb.id"
                class="flex items-center justify-center flex-shrink-0 w-16 h-12 px-2 border-b border-slate-400 bg-pure-white"
              >
                <BlTextBody1 :text="pb.code" />
              </div>
              <div
                v-if="dataFormat === 'distribution'"
                v-for="task in defaultTasks"
                :key="task.id"
                class="flex items-center justify-center flex-shrink-0 w-24 h-12 px-2 border-b border-slate-400 bg-pure-white"
              >
                <BlTextBody1 :text="task.code" />
              </div>
            </div>
            <div
              v-if="dataFormat === 'distribution'"
              id="pb-by-header"
              class="flex items-center justify-center flex-shrink-0 w-40 h-12 px-2 border-b border-slate-400 bg-pure-white"
            >
              <BlTextBody1 text="Problem behavior" />
            </div>
            <div
              v-if="dataFormat !== 'distribution'"
              id="recorded-by-header"
              class="flex items-center flex-shrink-0 w-32 h-12 px-2 border-b border-slate-400 bg-pure-white"
            >
              <BlTextBody1 text="Recorded by" />
            </div>
            <div id="menu-header" class="flex-shrink-0 w-12 ml-auto"></div>
          </div>
        </div>

        <draggable
          handle=".handle"
          @update.prevent="onMovePosition($event)"
          class="flex flex-col w-full"
          :class="{ 'pointer-events-none': readonly }"
        >
          <div
            v-for="entry in entries"
            :key="`entry-${entry.id}`"
            :id="entry.id"
            class="flex flex-col w-full"
          >
            <EntryPromptSbt
              :key="`prompting-sbt-entry-${entry.id}-${entry.position}`"
              :entry="entry"
              :promptColors="promptSbtColors"
              :tasks="defaultTasks"
              :problemBehaviors="defaultProblemBehaviors"
              :prompts="defaultPrompts"
              :dataFormat="dataFormat"
            />
          </div>
        </draggable>
      </div>
      <BlTextBody2
        class="w-full px-6 my-6 text-center"
        text="You have reached the end."
        style="opacity: 0.375"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import draggable from "vuedraggable";

import ProgressGraph from "./ProgressGraph.vue";
import Entry from "./Entry.vue";
import EntryFrequency from "./EntryFrequency.vue";
import EntryPrompt from "./EntryPrompt.vue";
import EntryPromptSbt from "./EntryPromptSbt.vue";
import EntryPhaseLine from "./EntryPhaseLine.vue";
import NewPhaseLineModal from "./modal/NewPhaseLineModal.vue";

import BlBubbleCount from "../../BlBubbleCount.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlIcon from "../../BlIcon.vue";

export default {
  name: "TargetProblemBehaviors",
  components: {
    draggable,
    ProgressGraph,
    Entry,
    EntryFrequency,
    EntryPrompt,
    EntryPromptSbt,
    EntryPhaseLine,
    NewPhaseLineModal,
    BlBubbleCount,
    BlTextBody1,
    BlTextBody2,
    BlTextSubtitle1,
    BlTextSubtitle3,
    BlInputButton,
    BlIcon
  },
  props: ["target", "graphEntries", "entries", "readonly"],
  data() {
    return {
      chartLoading: false,
      chartKey: "lorem-ipsum",
      chartDatas: {
        labels: [],
        datasets: [],
        recorded_by: []
      },
      chartTrendlineDatas: [],
      chartTrendlineShow: true,
      chartGridShow: true,
      chartLegendShow: true,
      defaultPrompts: [],
      defaultTasks: [],
      defaultProblemBehaviors: [],
      dataFormat: "cumulative",
      dataFormatOptions: [
        { value: "cumulative", title: "Cumulative" },
        { value: "distribution", title: "Distribution" }
      ],
      rearrangeState: false,
      newPhaseLineOpened: false,
      phaseLineColors: {
        lake: "#2AB1A8",
        tangerine: "#F57E00",
        cherry: "#FF4447",
        sky: "#487ADF",
        moss: "#A7C957",
        gold: "#F4D430",
        salmon: "#F4855D",
        primary: "#9A8AC2"
      },
      promptSbtColors: {
        moss: {
          primaryColor: "#A7C957",
          secondaryColor: "#A7C957",
          textColor: "#FFFFFF"
        },
        salmon: {
          primaryColor: "#F4855D",
          secondaryColor: "#F4855D",
          textColor: "#FFFFFF"
        },
        gold: {
          primaryColor: "#F4D430",
          secondaryColor: "#F4D430",
          textColor: "#FFFFFF"
        },
        lake: {
          primaryColor: "#2AB1A8",
          secondaryColor: "#2AB1A8",
          textColor: "#FFFFFF"
        },
        cherry: {
          primaryColor: "#FF4447",
          secondaryColor: "#FF4447",
          textColor: "#FFFFFF"
        },
        sky: {
          primaryColor: "#487ADF",
          secondaryColor: "#487ADF",
          textColor: "#FFFFFF"
        },
        lavender: {
          primaryColor: "#A533FF",
          secondaryColor: "#A533FF",
          textColor: "#FFFFFF"
        },
        tangerine: {
          primaryColor: "#F57E00",
          secondaryColor: "#F57E00",
          textColor: "#FFFFFF"
        }
      }
    };
  },
  computed: {
    ...mapGetters(["client"]),
    isPromptingShowAll() {
      let isShow = true;
      this.defaultPrompts.forEach(i => {
        if (!i.is_show) isShow = false;
      });
      return isShow;
    },
    isTaskShowAll() {
      let isShow = true;
      this.defaultTasks.forEach(i => {
        if (!i.is_show) isShow = false;
      });
      return isShow;
    },
    isPbShowAll() {
      let isShow = true;
      this.defaultProblemBehaviors.forEach(i => {
        if (!i.is_show) isShow = false;
      });
      return isShow;
    },
    phaseLineCount() {
      const phaseLines = this.entries.filter(i => i.type.includes("PhaseLine"));
      return phaseLines.length;
    }
  },
  watch: {
    target: {
      deep: false,
      handler(val, oldVal) {
        this.fetchGraphEntries();
        if (val.id !== oldVal.id) {
          this.rearrangeState = false;
        }
        if (val.type.includes("Prompting") || val.type.includes("Sbt")) {
          this.generatePrompts(val.prompts);
        }
        if (val.type.includes("Sbt")) {
          this.generateTasks(val.target_tasks);
          this.generateProblemBehaviors(val.target_problem_behaviors);
        }
      }
    },
    graphEntries: {
      deep: true,
      handler(val) {
        this.generateGraphEntriesData(val);
      }
    },
    chartTrendlineShow(val) {
      if (this.chartTrendlineDatas.length) {
        if (val) {
          this.chartTrendlineDatas.forEach(i => {
            this.chartDatas.datasets.push(i);
          });
        } else {
          this.chartTrendlineDatas.forEach(() => {
            this.chartDatas.datasets.pop();
          });
        }
        this.onResetChartKey();
      }
    },
    chartGridShow() {
      this.onResetChartKey();
    }
  },
  async mounted() {
    if (this.target.type.includes("Sbt")) {
      this.generatePrompts(this.target.prompts);
    }
    if (this.target.type.includes("Sbt")) {
      this.generateTasks(this.target.target_tasks);
      this.generateProblemBehaviors(this.target.target_problem_behaviors);
    }
    this.generateGraphEntriesData(this.graphEntries);
    await this.fetchGraphEntries();
  },
  methods: {
    ...mapActions(["getGraphEntries", "movePhaseLine"]),
    async fetchGraphEntries() {
      if (this.readonly) return;
      this.chartLoading = true;
      await this.getGraphEntries({ target_id: this.target.id });
      this.chartLoading = false;
    },
    generatePrompts(val = []) {
      this.defaultPrompts = val.map(i => ({ ...i, is_show: true }));
    },
    generateTasks(val = []) {
      this.defaultTasks = val.map(i => ({ ...i, is_show: true }));
    },
    generateProblemBehaviors(val = []) {
      this.defaultProblemBehaviors = val.map(i => ({ ...i, is_show: true }));
    },
    generateGraphEntriesData({
      labels = [],
      datasets = [],
      recorded_by: recordedBy = []
    }) {
      // generate datasets
      let newDatasets = [];
      if (
        datasets &&
        datasets.data &&
        datasets.data.tasks &&
        datasets.data.problem_behaviors
      ) {
        newDatasets = [
          ...this.defaultTasks.map(i => ({
            taskId: i.id,
            type: "line",
            label: i.code,
            data: [],
            backgroundColor: i.color + "30",
            borderColor: i.color + "30",
            pointBackgroundColor: i.color + "30",
            pointBorderColor: i.color + "30",
            pointRadius: 4,
            hoverRadius: 4,
            fill: false,
            tension: 0.1
          })),
          ...this.defaultProblemBehaviors.map(i => ({
            type: "bar",
            behaviorId: i.id,
            label: i.code,
            data: [],
            backgroundColor: i.color,
            borderColor: i.color,
            yAxisID: "y-axis-bar",
            barPercentage: 10,
            barThickness: 100,
            maxBarThickness: 100,
            minBarLength: 10
          }))
        ];
        datasets.data.tasks.forEach(entry => {
          this.defaultTasks.forEach((task, idx) => {
            let val = null;
            if (task.is_show && entry && entry[task.id]) {
              val = entry[task.id].score;
            }
            newDatasets[idx].data.push(val);
          });
        });
        datasets.data.problem_behaviors.forEach(entry => {
          this.defaultProblemBehaviors.forEach((behavior, idx) => {
            let val = null;
            if (behavior.is_show && entry && entry[behavior.id]) {
              val = entry[behavior.id].score;
            }
            newDatasets[this.defaultTasks.length + idx].data.push(val);
          });
        });
      }

      this.chartDatas = {
        labels: labels.map((i, idx) => {
          if (!i) return "";
          // label: YYYY-MM-DD
          const split = i.split("-").reverse();
          if (split.length === 3) {
            // return DD/MM \n YYYY
            return [`${split[0]}/${split[1]}`, split[2]];
          }
          // return DD/MM/YYY
          return split.join("/");
        }),
        datasets: newDatasets,
        recorded_by: recordedBy
      };
      this.onResetChartKey();
    },
    onResetChartKey() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let combination = "";
      for (let i = 0; i < 6; i++) {
        combination += chars[Math.round(Math.random() * 35)];
      }
      this.chartKey = combination;
    },
    onToggleTaskShow(data = "", type = "") {
      if (type === "task" && data === "allTasks") {
        const result = !this.isTaskShowAll;
        this.defaultTasks = this.defaultTasks.map(i => ({
          ...i,
          is_show: result
        }));
      } else if (type === "pb" && data === "allProblemBehaviors") {
        const result = !this.isPbShowAll;
        this.defaultProblemBehaviors = this.defaultProblemBehaviors.map(i => ({
          ...i,
          is_show: result
        }));
      } else if (type === "task" && data !== "allTasks") {
        const idx = this.defaultTasks.findIndex(i => i.id === data.id);
        if (idx > -1) {
          this.defaultTasks[idx].is_show = !this.defaultTasks[idx].is_show;
        }
      } else if (type === "pb" && data !== "allProblemBehaviors") {
        const idx = this.defaultProblemBehaviors.findIndex(
          i => i.id === data.id
        );
        if (idx > -1) {
          this.defaultProblemBehaviors[idx].is_show = !this
            .defaultProblemBehaviors[idx].is_show;
        }
      }
      this.generateGraphEntriesData(this.graphEntries);
    }
  }
};
</script>

<style scoped lang="scss">
.bl-target-progress-container {
  position: relative;
  width: 100%;
  // height: calc(100% - 3.5625rem);
  height: 100%;
  padding: 1.5rem 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  &.bl-target-progress-container-empty {
    padding: 0 30%;
    justify-content: center;
    align-items: center;
  }
  .bl-target-progress-message {
    position: relative;
    width: 100%;
    text-align: center;
    .bl-target-progress-message-highlight {
      cursor: pointer;
      position: relative;
      color: var(--primary-color);
    }
  }
  .bl-target-progress-graph {
    position: relative;
    width: 100%;
    display: flex;
    gap: 1rem;
    flex-direction: column;
    .bl-target-progress-graph-header {
      position: relative;
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
      .bl-target-progress-graph-title-container {
        position: relative;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        .bl-target-progress-graph-count {
          position: relative;
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 0.75rem;
          background-color: var(--primary-color);
          margin-left: 0.5rem;
          font-weight: 600;
          font-size: 0.75rem;
          line-height: 1;
          color: #fff;
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }
      .bl-graph-legend-container {
        .bl-tick-line {
          position: relative;
          width: 2.5rem;
          height: 0.25rem;
          margin-right: 0.5rem;
          display: flex;
          align-items: center;
          border-radius: 4px;
        }
      }
    }
    .bl-target-progres-graph-body {
      position: relative;
      width: 100%;
      height: 30rem;
    }
  }
  .bl-target-progress-entries {
    margin-top: 2rem;
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/clients/target/PropertyProblemBehaviors.vue